import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

export default function AssetsManager() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    isNFT: '',
    type: '',
    configType: '',
    inventoryType: '',
    status: '',
    rarity: '',
    contract: ''
  });

  // Fetch all assets and cache them
  const { data: assets = [], isLoading, isError } = useQuery({
    queryKey: ['allAssets'],
    queryFn: async () => {
      const response = await api.get('/inventories/items');
      return response.data;
    },
    staleTime: 5 * 60 * 1000
  });

  // Process assets to extract filter options and NFT status
  const processedAssets = useMemo(() => {
    return assets.map(asset => {
      let configType = null;
      let stackSize = asset.stackSize;
      let inventoryType = asset.inventoryType;
      let isNFT = false;
      let nftContract = null;
      let nftContractAddress = null;
      let nftTokenId = null;
      
      if (asset.config) {
        try {
          const configObj = typeof asset.config === 'string' ? JSON.parse(asset.config) : asset.config;
          if (configObj && configObj.type) configType = configObj.type;
          if (configObj && configObj.stackSize !== undefined) stackSize = configObj.stackSize;
          if (configObj && configObj.inventoryType) inventoryType = configObj.inventoryType;
          
          // Check for NFT indicators in config
          if (configObj && (
            configObj.isNFT === true ||
            configObj.nft === true ||
            configObj.NFT === true ||
            configObj.contract ||
            configObj.contractAddress ||
            configObj.tokenId ||
            (configObj.type && configObj.type.toLowerCase().includes('nft')) ||
            (configObj.category && configObj.category.toLowerCase().includes('nft'))
          )) {
            isNFT = true;
            nftContract = configObj.contract;
            nftContractAddress = configObj.contractAddress;
            nftTokenId = configObj.tokenId;
            
            // Also check metadata for contract_address
            if (configObj.metadata) {
              try {
                const metadataObj = typeof configObj.metadata === 'string' ? JSON.parse(configObj.metadata) : configObj.metadata;
                if (metadataObj && metadataObj.contract_address && !nftContractAddress) {
                  nftContractAddress = metadataObj.contract_address;
                }
              } catch (parseError) {
                // Invalid metadata JSON, skip
                console.warn('Failed to parse metadata JSON:', parseError);
              }
            }
          }
        } catch (configError) {
          // Invalid config JSON, skip this asset
          console.warn('Failed to parse asset config:', configError);
        }
      }
      
              return {
          ...asset,
          configType,
          stackSize,
          inventoryType,
          isNFT,
          nftContract,
          nftContractAddress,
          nftTokenId
        };
    });
  }, [assets]);

  // Extract unique filter options
  const filterOptions = useMemo(() => {
    const types = [...new Set(processedAssets.map(a => a.type).filter(Boolean))];
    const configTypes = [...new Set(processedAssets.map(a => a.configType).filter(Boolean))];
    const inventoryTypes = [...new Set(processedAssets.map(a => a.inventoryType).filter(Boolean))];
    const statuses = [...new Set(processedAssets.map(a => a.status).filter(Boolean))];
    const rarities = [...new Set(processedAssets.map(a => a.rarity).filter(r => r !== undefined && r !== null))];
    const contracts = [...new Set(processedAssets.map(a => a.nftContract).filter(Boolean))];
    
    return {
      types: types.sort(),
      configTypes: configTypes.sort(),
      inventoryTypes: inventoryTypes.sort(),
      statuses: statuses.sort(),
      rarities: rarities.sort(),
      contracts: contracts.sort()
    };
  }, [processedAssets]);

  // Filtered assets based on search and filters
  const filteredAssets = useMemo(() => {
    return processedAssets.filter(asset => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          (asset.title && asset.title.toLowerCase().includes(searchLower)) ||
          (asset.name && asset.name.toLowerCase().includes(searchLower)) ||
          (asset.id && asset.id.toLowerCase().includes(searchLower)) ||
          (asset.description && asset.description.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) return false;
      }
      
      // NFT filter
      if (filters.isNFT !== '') {
        if (filters.isNFT === 'true' && !asset.isNFT) return false;
        if (filters.isNFT === 'false' && asset.isNFT) return false;
      }
      
      // Type filter
      if (filters.type && asset.type !== filters.type) return false;
      
      // Config type filter
      if (filters.configType && asset.configType !== filters.configType) return false;
      
      // Inventory type filter
      if (filters.inventoryType && asset.inventoryType !== filters.inventoryType) return false;
      
      // Status filter
      if (filters.status && asset.status !== filters.status) return false;
      
      // Rarity filter
      if (filters.rarity !== '' && asset.rarity != filters.rarity) return false;
      
      // Contract filter
      if (filters.contract && asset.nftContract !== filters.contract) return false;
      
      return true;
    });
  }, [processedAssets, searchTerm, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      isNFT: '',
      type: '',
      configType: '',
      inventoryType: '',
      status: '',
      rarity: '',
      contract: ''
    });
  };

  const hasActiveFilters = searchTerm || Object.values(filters).some(v => v !== '');

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="h6">Loading assets...</Typography>
        </Stack>
      </Box>
    );
  }
  if (isError) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Alert severity="error">Failed to load assets.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
        Assets
      </Typography>
        <Typography variant="body2" color="textSecondary">
          {filteredAssets.length} of {processedAssets.length} assets
          {hasActiveFilters && ' (filtered)'}
        </Typography>
      </Stack>

      {/* Search Bar */}
      <Box mb={2}>
        <TextField
          fullWidth
          placeholder="Search assets by name, title, ID, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <Button
                  size="small"
                  onClick={() => setSearchTerm('')}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  <ClearIcon />
                </Button>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Filters */}
      <Accordion expanded={showFilters} onChange={() => setShowFilters(!showFilters)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterIcon />
            <Typography variant="h6">Filters</Typography>
            {hasActiveFilters && (
              <Chip label="Active filters" color="primary" size="small" />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
                     <Grid container spacing={3} mb={2}>
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth sx={{ minWidth: 200 }}>
                 <InputLabel>NFT Status</InputLabel>
                 <Select
                   value={filters.isNFT}
                   label="NFT Status"
                   onChange={(e) => handleFilterChange('isNFT', e.target.value)}
                 >
                   <MenuItem value="">All</MenuItem>
                   <MenuItem value="true">NFT Only</MenuItem>
                   <MenuItem value="false">Non-NFT Only</MenuItem>
                 </Select>
               </FormControl>
             </Grid>
             
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth sx={{ minWidth: 200 }}>
                 <InputLabel>Type</InputLabel>
                 <Select
                   value={filters.type}
                   label="Type"
                   onChange={(e) => handleFilterChange('type', e.target.value)}
                 >
                   <MenuItem value="">All</MenuItem>
                   {filterOptions.types.map(type => (
                     <MenuItem key={type} value={type}>{type}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth sx={{ minWidth: 200 }}>
                 <InputLabel>Subtype</InputLabel>
                 <Select
                   value={filters.configType}
                   label="Subtype"
                   onChange={(e) => handleFilterChange('configType', e.target.value)}
                 >
                   <MenuItem value="">All</MenuItem>
                   {filterOptions.configTypes.map(type => (
                     <MenuItem key={type} value={type}>{type}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth sx={{ minWidth: 200 }}>
                 <InputLabel>Inventory Type</InputLabel>
                 <Select
                   value={filters.inventoryType}
                   label="Inventory Type"
                   onChange={(e) => handleFilterChange('inventoryType', e.target.value)}
                 >
                   <MenuItem value="">All</MenuItem>
                   {filterOptions.inventoryTypes.map(type => (
                     <MenuItem key={type} value={type}>{type}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth sx={{ minWidth: 200 }}>
                 <InputLabel>Status</InputLabel>
                 <Select
                   value={filters.status}
                   label="Status"
                   onChange={(e) => handleFilterChange('status', e.target.value)}
                 >
                   <MenuItem value="">All</MenuItem>
                   {filterOptions.statuses.map(status => (
                     <MenuItem key={status} value={status}>{status}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth sx={{ minWidth: 200 }}>
                 <InputLabel>Rarity</InputLabel>
                 <Select
                   value={filters.rarity}
                   label="Rarity"
                   onChange={(e) => handleFilterChange('rarity', e.target.value)}
                 >
                   <MenuItem value="">All</MenuItem>
                   {filterOptions.rarities.map(rarity => (
                     <MenuItem key={rarity} value={rarity}>{rarity}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth sx={{ minWidth: 200 }}>
                 <InputLabel>Contract</InputLabel>
                 <Select
                   value={filters.contract}
                   label="Contract"
                   onChange={(e) => handleFilterChange('contract', e.target.value)}
                 >
                   <MenuItem value="">All</MenuItem>
                   {filterOptions.contracts.map(contract => (
                     <MenuItem key={contract} value={contract}>{contract}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
           </Grid>
          
          <Button variant="outlined" onClick={handleClearFilters} disabled={!hasActiveFilters}>
            Clear All Filters
          </Button>
        </AccordionDetails>
      </Accordion>

             {/* Table */}
       <Card sx={{ mt: 2 }}>
         <TableContainer>
           <Table>
                           <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Subtype</TableCell>
                  <TableCell>Stack Size</TableCell>
                  <TableCell>Inventory Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rarity</TableCell>
                  <TableCell>Unity Asset</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
             <TableBody>
               {filteredAssets.map((asset) => (
                                   <TableRow key={asset.id}>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight="medium">
                            {asset.title || asset.name || 'Untitled'}
                          </Typography>
                          {asset.isNFT && (
                            <Chip 
                              label="NFT" 
                              size="small" 
                              color="success" 
                              variant="filled"
                              sx={{ 
                                fontWeight: 'bold',
                                backgroundColor: '#4caf50',
                                color: 'white',
                                height: 20
                              }}
                            />
                          )}
                        </Stack>
                        {(asset.nftContract || asset.nftContractAddress) && (
                          <>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                              Contract
                            </Typography>
                            {asset.nftContract && (
                              <Typography variant="caption" color="info.main">
                                {asset.nftContract}
                              </Typography>
                            )}
                            {asset.nftContractAddress && (
                              <Typography variant="caption" color="info.main" 
                                sx={{ 
                                  fontFamily: 'monospace',
                                  wordBreak: 'break-all'
                                }}
                              >
                                {asset.nftContractAddress}
                              </Typography>
                            )}
                          </>
                        )}
                        {asset.nftTokenId && (
                          <Typography variant="caption" color="info.main">
                            Token: {asset.nftTokenId}
                          </Typography>
                        )}
                        {asset.description && (
                          <Typography variant="caption" color="textSecondary" 
                            sx={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              maxWidth: '250px'
                            }}
                          >
                  {asset.description}
                </Typography>
                        )}
                </Stack>
                    </TableCell>
                    
                    <TableCell>
                      <Tooltip title={asset.id}>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 100, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          fontFamily: 'monospace'
                        }}>
                          {asset.id}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    
                    <TableCell>
                      {asset.type && (
                        <Chip label={asset.type} size="small" />
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {asset.configType && (
                        <Chip label={asset.configType} size="small" color="primary" />
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {asset.stackSize !== undefined && asset.stackSize !== null && asset.stackSize !== 0 && (
                        <Typography variant="body2">
                          {asset.stackSize}
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {asset.inventoryType && (
                        <Chip label={asset.inventoryType} size="small" color="secondary" />
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {asset.status && (
                        <Chip label={asset.status} size="small" />
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {asset.rarity !== undefined && asset.rarity !== null && (
                        <Typography variant="body2">
                          {asset.rarity}
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {asset.unityAssetId && (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {asset.unityAssetId}
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {asset.createdAt ? new Date(asset.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {asset.updatedAt ? new Date(asset.updatedAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                    </TableCell>
                  </TableRow>
               ))}
             </TableBody>
           </Table>
         </TableContainer>
            </Card>
    </Box>
  );
} 